<template>
  <div class="column-stretch-wrap pad-bottom-twenty">
    <div class="column-stretch-header">
      <ion-grid>
        <ion-row class="ion-align-items-center pad-twenty">
          <ion-col>
            <ion-row class="form-control-search search-secondary ion-align-items-center">
              <ion-col>
                <input type="search" class="full-width" v-model="searchString" placeholder="Search for patient..." @input="debounceThis($event)" @keyup.enter="searchInput()" />
              </ion-col>
              <ion-col size="auto">
                <span id="search">
                  <ion-spinner v-if="showSpinner" name="lines" class="spinner-medium valign margin-right-ten"></ion-spinner>
                  <IconSearch v-else-if="!searchString" class="icon-small icon-primary margin-right-ten" />
                  <button v-else @click="clearSearch()" id="clear-btn"><ion-icon id="clear-icon" name="md-close" class="icon-small icon-primary margin-right-five margin-top-five"></ion-icon></button>
                </span>
              </ion-col>
            </ion-row>
          </ion-col>
        </ion-row>
      </ion-grid>
    </div>
    <div class="column-stretch-body">
      <ion-content class="prime-popover" v-if="showPopover">
        <div v-if="emptyResults" class="no-results text-muted text-small text-center center-center">
          There are no results to display
        </div>
        <ion-list v-else class="pad-five">
          <ion-item lines="none" v-for="(patient, key) in searchResults" :key="key" @click="makeSelection(patient)" class="prime-patient-card ion-margin-bottom margin-lr-ten" :class="{ 'cursor-pointer': patient.enrollmentStatus == 'approved' }">
            <div class="avatar" v-if="patient.beneFirstName && patient.beneLastName">
              {{ patient.beneFirstName.charAt(0) + patient.beneLastName.charAt(0) }}
            </div>
            <div>
              <div class="prime-patient-card-name">
                {{ patient.beneLastName + ", " + patient.beneFirstName }}
              </div>
              <div class="prime-patient-card-info"><strong>ID: </strong>{{ patient.mrn }} <strong>DOB: </strong>{{ patient.dobStr }}</div>
            </div>
            <div class="prime-patient-card-icons">
              <IconChatBubbleDisabled v-if="patient.enrollmentStatus != 'approved'" class="icon-blue icon-small" v-tooltip="'This patient is not currently enrolled in LumerisEngage.'" />
            </div>
          </ion-item>
        </ion-list>
        <ion-infinite-scroll @ionInfinite="lazyLoad($event)" threshold="10px" id="infinite-scroll" :disabled="endOfSearch">
          <ion-infinite-scroll-content loading-spinner="lines" loading-text="Loading more results..."> </ion-infinite-scroll-content>
        </ion-infinite-scroll>
      </ion-content>
    </div>
    <div class="column-stretch-footer top-divider pad-top-twenty pad-lr-twenty">
      <ion-row>
        <ion-col></ion-col>
        <ion-col size-xs="12" size-sm="8" size-md="6">
          <button @click="closeModal()" class="prime-button button-secondary button-block">
            Close
          </button>
        </ion-col>
        <ion-col></ion-col>
      </ion-row>
    </div>
  </div>
</template>

<script>
import IconSearch from "../Global/Icons/IconSearch.vue";
import IconChatBubbleDisabled from "@/components/Global/Icons/IconChatBubbleDisabled";
import { send as httpSend } from "@/services/Api";
import { EventBus } from "@/services/Events.js";
import router from "@/router";
import store from "@/store";
import _ from "lodash";

export default {
  name: "ModalSearchPatients",
  components: {
    IconSearch,
    IconChatBubbleDisabled
  },
  props: {
    title: String
  },
  data() {
    return {
      errors: [],
      showSpinner: false,
      emptyResults: false,
      endOfSearch: false,
      searchString: "",
      showPopover: false,
      searchResults: [],
      searchPage: 1,
      perPage: 20
    };
  },
  methods: {
    closeModal() {
      this.$ionic.modalController.dismiss();
    },
    debounceThis: _.debounce(function(evt) {
      this.searchBoxPosition = evt.target.offsetLeft;
      this.searchInput();
    }, 400),
    async searchInput() {
      // keeps selected message panel visible after searching
      this.searchPage = 1;
      this.searchResults = [];
      this.endOfSearch = false;
      this.showSpinner = true;
      this.showPopover = false;
      if (this.searchString.length > 2) {
        this.searchCall();
      } else {
        this.showSpinner = false;
      }
    },
    async searchCall(responseStyle, evt) {
      const method = "post";
      const claims = await this.$authState.claims();
      const path = document.config.patientsApi + claims.professionalId;

      var payload = {
        page: this.searchPage,
        perPage: this.perPage,
        columnFilters: {
          beneLastName: this.searchString,
          beneFirstName: this.searchString,
          mrn: this.searchString,
          dob: this.searchString,
          active: "true",
          orgId: this.$authState.getOrgId() ? this.$authState.getOrgId() : claims.orgIds
        },
        sort: [{ field: "beneLastName", type: "asc" }]
      };

      httpSend({ path, method, authToken: true, payload })
        .then(result => {
          this.showPopover = true;
          if (responseStyle == "combine") {
            this.searchResults = [...this.searchResults, ...result.data.results];
          } else {
            this.searchResults = result.data.results;
          }
          result.data.results.length < this.perPage ? (this.endOfSearch = true) : "";

          result.data.results.length == 0 ? (this.emptyResults = true) : (this.emptyResults = false);

          evt ? evt.target.complete() : "";
        })
        .catch(error => {
          khanSolo.log(error);
        })
        .finally(() => {
          this.searchPage += 1;
          this.showSpinner = false;
        });
    },
    lazyLoad(ev) {
      if (!this.endOfSearch) {
        this.searchCall("combine", ev);
      }
    },
    clearSearch() {
      this.searchString = "";
      document.querySelector("#search input").focus();
      this.searchResults = [];
    },
    makeSelection(patient) {
      if (patient.enrollmentStatus != "approved") {
        return;
      }
      this.clearSearch();

      store.commit("setPanelFlow", { prevPanel: ".prime-active-conversations-container", currentPanel: "#convos-message" }, { root: true });
      this.$mobilize(".prime-active-conversations-container", "#convos-message", "mobile-show");

      // jump to conversations page if not already there
      if (router.currentRoute.name !== "patientView") {
        router.push({
          name: "patientView",
          query: { patient: patient.beneficiaryId }
        });
      } else {
        history.pushState(null, null, router.currentRoute.path + "?patient=" + patient.beneficiaryId);
        EventBus.$emit("selectedPatientId", patient.beneficiaryId);
        router.currentRoute.query.patient = patient.beneficiaryId;
      }

      this.closeModal();
    }
  }
};
</script>

<style scoped>
.prime-patient-card {
  background: #ffffff;
  color: var(--ion-text-color);
  display: flex;
  align-items: center;
  margin: 0 0 10px 0 !important;
  box-shadow: 0 1px 6px 0px rgba(46, 61, 73, 0.2) !important;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border-radius: 8px;
  padding: 5px 0 5px 0;
}
.prime-patient-card:hover {
  box-shadow: 0 3px 15px 0px rgba(46, 61, 73, 0.3) !important;
}
.prime-patient-card .avatar {
  min-width: 40px;
  height: 40px;
  font-size: 18px;
  margin-left: 0px;
  margin-right: 10px;
}
.prime-patient-card .prime-patient-card-name {
  text-align: left;
  font-size: 16px;
  font-weight: 500;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.prime-patient-card .prime-patient-card-info {
  font-size: 11px;
  font-weight: 500;
  margin-top: 2px;
  color: var(--ion-color-medium-shade);
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.prime-patient-card .prime-patient-card-info strong:not(:first-child) {
  margin-left: 10px;
}
.prime-patient-card .prime-patient-card-icons {
  display: flex;
  position: absolute;
  right: 5px;
  top: 0px;
}
.prime-patient-card .prime-patient-card-icons img {
  width: 15px;
  margin: 0 0 0 10px;
}

.form-control-search.search-secondary {
  min-height: 46px;
}
</style>
